import { useMemo, useState, type FC } from 'react';
import type { HeroDetailedProps } from './types';
import { shade } from 'shared/helpers/utils';

import styled, { css } from 'styled-components';
import {
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText,
  ApiService,
  baseMediaEndpoint,
  COLOR_YELLOW
} from 'shared/helpers/index';
import { useSales, useWebsiteEditor, useAlert } from 'shared/hooks/index';
import { useMutation } from 'react-query';

import { Button, Wrapper, Download } from '../Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { FlexBox, Image as ImageComp } from 'shared/components/Common';
import { HoverableDropzone } from '../HoverableDropzone';
import ButtonActionModal from 'shared/components/Common/ButtonActionModal';

const HeroDetailed = styled.div<{ hasBenefits?: boolean; isEditable?: boolean }>`
  position: relative;
  padding: 140px 0 0;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    padding: 136px 0 0;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    padding: 155px 0 0px;
    `}
`;

const StyledWrapper = styled((props) => <Wrapper {...props} />)`
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  max-width: 100%;
  gap: 50px;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    flex-direction: column;
    gap: 15px;
    `}
`;

const TitleWrapper = styled.div`
  width: 820px;
  display: flex;
  flex-direction: column;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 60%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 20px;
    `}
`;

const Title = styled.h1<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: 60px;
  margin-bottom: 35px;
  line-height: 1.15em;
  margin-top: 0;

  ${({ theme }: { theme: { $width: number; $largeHeadlineFont: string } }) =>
    theme.$width <= 1254 &&
    `
    font-size: ${theme.$largeHeadlineFont};
    margin-bottom: 25px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-bottom: 30px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Description = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: 32px;
  margin-bottom: 96px;
  line-height: 1.35em;

  ${({ theme }: { theme: { $width: number; $paragraphFont: string } }) =>
    theme.$width <= 1254 &&
    `
    font-size: ${theme.$paragraphFont};
    margin-bottom: 50px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-bottom: 45px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-bottom: 50px;
    `}
  
  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const StyledButton = styled(Button)`
  max-width: 465px;
  display: inline-block;
  padding: 28px 40px;
  border-radius: 40px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 395px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 345px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    padding-top: 24px;
    padding-bottom: 26px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 100%;
    `}
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
  height: 100%;
  width: 100%;
`;

const BookMediaImg = styled(ImageComp)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 92%;
  right: 0;
  margin: 0 auto;
  z-index: 1;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
      height: 83%;
    `}
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(91deg, rgba(33, 33, 33, 0.29) 37.42%, rgba(33, 33, 33, 0.1) 65.51%);
`;

const StyledWrapperBenefits = styled((props) => <Wrapper {...props} />)`
  padding: 0px 0px 10px;
  margin-top: 200px;
  position: relative;
  z-index: 5;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
      margin-top: 150px;
      `}
`;

const BenefitWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  z-index: 12;
  bottom: 20px;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
      flex-direction: column;
      gap: 15px;
      `}
`;

const BenefitItem = styled.div`
  width: 31.5%;
  padding: 17px 22px 28px;
  border-radius: 17px;
  background: #fff;
  display: flex;
  gap: 18px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 32%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    flex-direction: row;
    align-items: flex-start;
    text-align: left;
    padding: 17px 13px 20px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    padding: 18px 18px 20px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    padding: 18px 15px;
    border-radius: 12px;
    `}
`;

const Svg = styled.svg`
  width: 82px;
  height: 82px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      width: 52px;
      height: 52px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 42px;
    height: 42px;
    margin-bottom: 8px;
  `}

    ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 52px;
    height: 52px;
    margin-bottom: 8px;
  `}
`;

const BenefitTxtInner = styled.div`
  width: 100%;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 76%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
      width: 80%;
    `}
`;

const TitleBenefit = styled.h3<{ isEmpty?: boolean; editable?: boolean }>`
  margin-bottom: 4px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  font-weight: 500;
  line-height: 1.35em;

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    margin-bottom: 4px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
      display: ${editable ? 'block' : 'none'};
      &:not(:focus):before {
        content: attr(data-placeholder);
        cursor: text;
        color: #aaa;
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    `
      : `
      &:before {
        content: none;
      } 
    `}
`;

const TextBenefit = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  line-height: 1.3em;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

export const V2: FC<HeroDetailedProps> = (props) => {
  const {
    title = defaultWebValue,
    content = defaultWebValue,
    button = defaultWebValue,
    textColor,
    secondaryColor = COLOR_YELLOW,
    benefits = [],
    meta,
    hidden
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();

  const { addAlert } = useAlert();
  const { purchaseElementId, updateImageMeta, type, salesPageId } = useSales();
  const [isButtonActionModalOpen, setIsButtonActionmodalOpen] = useState(false);

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);
  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  const { mutateAsync: uploadeImage, isLoading } = useMutation({
    mutationFn: (payload: FormData) =>
      ApiService.post(`/sales/sales-page/${salesPageId}/media/`, payload),
    onError: () => {
      addAlert('Please upload png, jpg and jpeg image only', 'warning');
    }
  });

  const aboutMeImage = useMemo(() => {
    if (meta['image'] || meta['image_1']) {
      if (baseMediaEndpoint?.includes('localhost'))
        return `${baseMediaEndpoint}${meta['image'] || meta['image_1']}`;
      return meta['image'];
    }
    return '/img/bg_gal_1.jpg';
  }, [meta]);

  const handleUpload = async (file: File) => {
    if (!meta['accessorKey'] || !editable) return;

    try {
      const formData = new FormData();
      formData.append('type', meta['accessorKey']);
      formData.append('file', file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        // img.onload = () => {
        //   const width = img.width;
        //   const height = img.height;
        //   setResize({ width, height });
        //   updateImageMeta(meta['accessorKey'], width, 'width');
        //   updateImageMeta(meta['accessorKey'], height, 'height');
        // };
        img.src = e.target?.result as string;
      };

      reader.readAsDataURL(file);

      const { data } = await uploadeImage(formData);
      updateImageMeta(meta['accessorKey'], data.file, 'image');
    } catch (_error) {
      addAlert('Something went wrong', 'error');
    }
  };

  const shadedBackgroundColor = useMemo(() => {
    return shade(secondaryColor, 0.7);
  }, [secondaryColor]);

  return (
    <HeroDetailed
      isEditable={process.env['APP_TYPE'] === 'dashboard'}
      hasBenefits={benefits.length > 0}
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1
      }}>
      <StyledWrapper className="title-wrapper">
        <TitleWrapper>
          <Title
            id={title.id}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, process.env['APP_TYPE'] === 'dashboard')
            }}
            style={{ color: filterBackgroundColor('rgba(45, 45, 45, 0.43)', textColor) }}
            onClick={updateActiveElement}
            onBlur={resetActiveElement}
            data-accessor-parent={meta['accessorKey']}
            data-accessorkey="title"
            data-placeholder="+"
            editable={process.env['APP_TYPE'] === 'dashboard'}
            isEmpty={isTitleEmpty}
          />
          <Description
            id={content.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
            style={{ color: filterBackgroundColor('rgba(45, 45, 45, 0.43)', textColor) }}
            onClick={updateActiveElement}
            onBlur={resetActiveElement}
            data-accessor-parent={meta['accessorKey']}
            data-accessorkey="content"
            data-placeholder="+"
            editable={process.env['APP_TYPE'] === 'dashboard'}
            isEmpty={isContentEmpty}
          />
          <FlexBox position="relative">
            {isButtonActionModalOpen && editable ? (
              <ButtonActionModal
                onClose={() => setIsButtonActionmodalOpen(false)}
                meta={meta}
                cssContainer={css`
                  position: absolute;
                  top: -450px;
                  left: -40px !important;
                  transform: translateY(100%);
                `}
              />
            ) : null}
            {meta['url'] || type === 'delivery' ? (
              <Download
                textColor={textColor}
                button={button}
                meta={meta}
                outerOnClick={() => (editable ? setIsButtonActionmodalOpen(true) : null)}
              />
            ) : (
              <StyledButton
                to={purchaseElementId}
                button={button}
                meta={meta}
                outerOnClick={() => (editable ? setIsButtonActionmodalOpen(true) : null)}
              />
            )}
          </FlexBox>
        </TitleWrapper>
      </StyledWrapper>

      <BackgroundImage>
        <HoverableDropzone
          isSalePage
          css={imageStyles as never}
          disabled={!editable}
          allowedExtensions={['image/png', 'image/jpg', 'image/jpeg']}
          onUpload={handleUpload}
          loading={isLoading}
          alreadyUploaded={Boolean(aboutMeImage)}
          imageFile={aboutMeImage}
          // resizeSelector={editable}
          // handleResize={({ width, height }) => {
          //   setResize({ width, height });
          //   updateImageMeta(meta['accessorKey'], width, 'width');
          //   updateImageMeta(meta['accessorKey'], height, 'height');
          // }}
          // initialPosition={{
          //   x: meta['x_1'] || 0,
          //   y: meta['y_1'] || 0,
          //   width: meta['d_width_1'],
          //   height: meta['d_height_1']
          // }}
          meta={meta}
          // positionEnabled
          // handlePosition={(width: number, height: number, x: number, y: number) => {
          //   updateImageMeta(meta['accessorKey'], x, 'x_1');
          //   updateImageMeta(meta['accessorKey'], y, 'y_1');
          //   updateImageMeta(meta['accessorKey'], width, 'd_width_1');
          //   updateImageMeta(meta['accessorKey'], height, 'd_height_1');
          // }}
          imageAspectRatio={{
            ratio: '16x9',
            pixels: '1920x1080'
          }}>
          <Overlay />
          <BookMediaImg
            // isImageUploaded={isImageUploaded}
            src={aboutMeImage}
            width={500}
            height={500}
            alt="Book Mockup"
          />
        </HoverableDropzone>
      </BackgroundImage>

      {benefits.length > 0 && (
        <StyledWrapperBenefits style={{ padding: '0px 0px 10px' }}>
          <BenefitWrapper>
            {benefits.map(({ content, icon, title }, index) => {
              const isTitleBenEmpty = checkTitleBenefits(index);
              const isContentBenEmpty = checkContentBenefits(index);
              return (
                <BenefitItem
                  style={{
                    backgroundColor: shadedBackgroundColor
                    // display: isTitleBenEmpty && isContentBenEmpty && !editable ? 'flex' : 'none'
                  }}
                  key={`${icon?.toString()}-${index}`}>
                  <Svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62 62">
                    <circle cx="31" cy="31" r="31" strokeWidth={0} fill={button.color} />
                    <path
                      d="m44.85,23.17c-.13.64-.53,1.1-.97,1.55-5.07,5.21-10.13,10.43-15.19,15.65-.4.41-.85.64-1.41.46-.23-.07-.46-.21-.63-.38-2.52-2.58-5.03-5.17-7.54-7.76-.86-.89-.84-2.14.01-3.03.28-.3.57-.59.86-.88.87-.88,2.08-.88,2.94,0,1.5,1.54,3,3.09,4.5,4.63.07.07.1.16.2.31.12-.17.17-.26.25-.34,4.15-4.28,8.3-8.56,12.46-12.84.98-1.01,2.15-1.01,3.13,0,.14.14.27.29.42.43.46.43.86.91.99,1.57v.64h0Z"
                      fill="#fff"
                      strokeWidth={0}
                    />
                  </Svg>
                  <BenefitTxtInner>
                    <TitleBenefit
                      id={title?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(title?.val, editable) }}
                      style={{ color: filterBackgroundColor('#fff', textColor) }}
                      onClick={updateActiveElement}
                      onBlur={resetActiveElement}
                      data-accessor-parent={meta['accessorKey']}
                      data-accessorkey="title"
                      data-benefits={true}
                      data-placeholder="+"
                      editable={process.env['APP_TYPE'] === 'dashboard'}
                      isEmpty={isTitleBenEmpty}
                    />
                    <TextBenefit
                      id={content?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(content?.val, editable) }}
                      style={{ color: filterBackgroundColor('#fff', textColor) }}
                      onClick={updateActiveElement}
                      onBlur={resetActiveElement}
                      data-accessor-parent={meta['accessorKey']}
                      data-accessorkey="content"
                      data-benefits={true}
                      data-placeholder="+"
                      editable={process.env['APP_TYPE'] === 'dashboard'}
                      isEmpty={isContentBenEmpty}
                    />
                  </BenefitTxtInner>
                </BenefitItem>
              );
            })}
          </BenefitWrapper>
        </StyledWrapperBenefits>
      )}
    </HeroDetailed>
  );
};
